import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Download, Loader2, Layers } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const ScriptCard = ({ s, getAuthHeader }) => {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/scripts/download/${encodeURIComponent(s.filename)}`,
        { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = s.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fehlgeschlagen'); }
    finally { setDownloading(false); }
  };

  return (
    <div className={`bg-cream-50 border rounded-2xl p-6 flex items-start gap-4 ${s.highlight ? 'border-terra shadow-sm' : 'border-cream-300'}`}
      data-testid={`script-card-${s.key}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${s.highlight ? 'bg-terra text-cream-50' : 'bg-espresso text-cream-50'}`}>
        {s.highlight ? <Layers className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra">
          {s.highlight ? 'Gesamtskript · PDF' : 'Kurs-Skript · PDF'}
        </p>
        <h3 className="font-display text-xl text-espresso mt-0.5">{s.title}</h3>
        <p className="text-xs text-sand-500 mt-1.5">
          {s.modules} Module · {s.ue} UE
          {s.exists && <> · {fmtSize(s.size_bytes)} · Stand {s.modified}</>}
        </p>
      </div>
      {s.exists ? (
        <button onClick={download} disabled={downloading}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-espresso text-cream-50 hover:opacity-90 shrink-0"
          data-testid={`script-download-${s.key}`}>
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
        </button>
      ) : (
        <span className="px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-cream-300 text-sand-500 shrink-0"
          data-testid={`script-pending-${s.key}`}>
          Wird erstellt …
        </span>
      )}
    </div>
  );
};

export default function AdminScriptsPage({ Layout }) {
  const { getAuthHeader } = useAuth();
  const [scripts, setScripts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/scripts`, getAuthHeader())
      .then((r) => setScripts(r.data.scripts))
      .catch(() => toast.error('Skripte konnten nicht geladen werden'));
  }, [getAuthHeader]);

  return (
    <Layout>
      <div className="max-w-4xl" data-testid="admin-scripts-page">
        <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra">Admin · Bibliothek</p>
        <h1 className="font-display text-3xl sm:text-4xl text-espresso mt-1">Skripte</h1>
        <p className="text-sm text-sand-500 mt-2 mb-8">
          Alle Kurs-Skripte als fertig gestaltete PDFs — vorgeneriert und sofort zum Herunterladen.
        </p>
        {!scripts ? (
          <div className="flex items-center gap-2 text-sand-500 text-sm py-12" data-testid="scripts-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Lade Skripte …
          </div>
        ) : (
          <div className="space-y-4">
            {scripts.map((s) => <ScriptCard key={s.key} s={s} getAuthHeader={getAuthHeader} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
